import { Fragment, useEffect, useState } from 'react'
import { Box, Button, TextField } from '@mui/material'
import { TopTitle } from '../components/atoms/TopTitle'
import { callAxios } from '../hooks/useAxios'
import { CIStatus } from '../types/testing'
import { PageTitle } from '../components/atoms/PageTitle'
import { useApp } from '../hooks/useApp'
import { TimeString } from '../types/models'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { nowutc, setCIBaseTime, setCISpeed } from '../utils/time'
import { DateTime } from 'luxon'

export function CIControlPage(): React.ReactElement {
  const [ci, setCI] = useState<Partial<CIStatus>>({
    ci: false,
    time: nowutc().toISO() as TimeString,
    speed: 1,
  })
  const [time, setTime] = useState('')
  const [runTimer, setRunTimer] = useState(false)

  const app = useApp()
  const updateCI = () => {
    callAxios<CIStatus>({
      url: '/ci',
    }).then(res => res.success && setCI(res.data))
  }

  const timerCI = () => {
    runTimer && updateCI()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => updateCI(), [])
  useEffect(() => {
    const interval = setInterval(timerCI, 1000)
    return () => clearInterval(interval)
  })

  const onControl = (name: string, value: string | number | number[]) => {
    callAxios<CIStatus>({
      method: 'PATCH',
      url: '/ci',
    }, { name, value }).then(res => res.success ? app.addInfo(`Adjusted ${name}`) : app.addError(res))
  }
  const onSpeed = (speed: number) => {
    onControl('speed', speed)
    setCISpeed(speed)
    setRunTimer(true)
  }

  const onTimeUpdate = () => {
    onControl('time', time)
    setCIBaseTime(DateTime.fromISO(time))
    setRunTimer(true)
  }

  const onTimeClear = () => {
    onControl('time', DateTime.utc().toISO())
    onControl('speed', 1)
    setCIBaseTime(null)
    setTime('')
    setRunTimer(false)
  }

  return (
    <Fragment>
      <TopTitle title={'Continuous Integration'}/>
      {!ci.ci &&
        <Box sx={{ color: 'primary.main' }}>
          This server does not run CI right now.
        </Box>
      }
      {ci.ci &&
        <Box>
          <PageTitle sx={{ mb: 2 }} title={'Status'}/>
          <Box>Browser Clock: {nowutc().toISO()}</Box>
          <Box>Server Clock: {ci.time}</Box>
          <Box>Speed: {ci.speed}</Box>
          <Box>Area: {ci.test_area}</Box>
          <Box>Price Source: {ci.test_source}</Box>
          <Box>Prices: {ci.spot_prices?.join(', ')}</Box>
          <Box>Users: {ci.users}</Box>

          <PageTitle sx={{ mt: 4, mb: 2 }} title={'Controls'}/>

          <Box sx={{ mt: 3, mb: 1 }}>
            <TextField
              data-cy="text-Change Time"
              color="secondary"
              variant="outlined"
              id="time"
              label={'Change Time'}
              name="time"
              autoFocus
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </Box>
          <Button data-cy="button-Set" onClick={() => onTimeUpdate()}>Set</Button>
          <Button data-cy="button-Clear" onClick={() => onTimeClear()} sx={{ ml: 2 }}>Clear</Button>

          <Box sx={{ mt: 4, mb: 2 }}>
            <PageSubtitle title={'Speed up time'}/>
            <Box>
              <Button data-cy="button-1 X" onClick={() => onSpeed(1)} sx={{ backgroundColor: 'secondary.main', mr: 5 }}>1 X</Button>
              Normal speed
            </Box>
            <Box sx={{ mt: 1 }}>
              <Button data-cy="button-15 X" onClick={() => onSpeed(15)} sx={{ backgroundColor: 'secondary.main', mr: 5 }}>15 X</Button>
              One minute in 4 seconds
            </Box>
            <Box sx={{ mt: 1 }}>
              <Button data-cy="button-3600 X" onClick={() => onSpeed(3600)} sx={{ backgroundColor: 'secondary.main', mr: 5 }}>3600 X</Button>
              One hour in 1 second
            </Box>
          </Box>

          <Box sx={{ mt: 4, mb: 2 }}>
            <PageSubtitle title={'Market Prices'}/>
            <Button
              data-cy="button-Test Set 1"
              onClick={() => onControl('spot_prices', [
                4, 5, 6, 6, 7, 8,
                10, 12, 13, 16, 19, 22,
                20, 20, 19, 18, 17, 17,
                11, 10, 5, 5, 3, 3,
              ])}
              sx={{ backgroundColor: 'green', mr: 5 }}>Test Set 1</Button>
          </Box>
        </Box>
      }
    </Fragment>
  )
}
